/**
 * AgedDemoPreview — scripted "aged" preview for a /mycraftz demo card.
 *
 * Lets a visitor see what accumulated practice looks like BEFORE committing:
 *   • "See it after a week"  → day-7 partial streak (a miss doesn't break it)
 *   • "See it after a month" → day-30 finished cycle + the Siddhi retrospective
 *
 * Everything here is ILLUSTRATIVE fixture data (see agedDemoFixtures.js) —
 * fixed, browser-local, never live-computed or tied to a real account. The
 * identity statement is editable purely for feel; edits live in local state.
 */
import React, { useState } from "react";
import { CalendarClock, CalendarCheck, RotateCcw, Flame, LifeBuoy, Target, Sparkles, Lock } from "lucide-react";
import { getAgedFixture } from "../../data/agedDemoFixtures";

export { getAgedFixture };

function DayStrip({ days }) {
  return (
    <div className="adp-strip" data-testid="adp-daystrip" aria-hidden="true">
      {days.map((kept, i) => (
        <span key={i} className={`adp-dot ${kept ? "is-kept" : "is-miss"}`} title={`Day ${i + 1}: ${kept ? "kept" : "missed"}`} />
      ))}
    </div>
  );
}

function Disclaimer() {
  return (
    <p className="adp-disclaimer" data-testid="adp-disclaimer">
      <Sparkles size={11} strokeWidth={1.7} />
      This is an example of what your practice could look like — not your real data.
    </p>
  );
}

function WeekPanel({ fx, slug }) {
  const w = fx.week;
  return (
    <div className="adp-panel" data-testid={`adp-week-${slug}`}>
      <Disclaimer />
      <div className="adp-sadhana">
        <div className="adp-streak-row">
          <span className="adp-streak"><Flame size={15} strokeWidth={1.8} /> {w.keptLabel}</span>
          <span className="adp-daytag">Day 7 · mid-stream</span>
        </div>
        <DayStrip days={w.days} />
        <p className="adp-practice">{fx.practiceLine}</p>

        <div className="adp-coping" data-testid={`adp-coping-${slug}`}>
          <p className="adp-coping-head"><LifeBuoy size={12} strokeWidth={1.8} /> A miss, handled — Day {w.coping.day}</p>
          <p className="adp-coping-line"><span className="adp-tag adp-tag-vighna">Vighna</span> {w.coping.vighna}</p>
          <p className="adp-coping-line"><span className="adp-tag adp-tag-upaya">Upaya</span> {w.coping.upaya}</p>
        </div>
      </div>

      <div className="adp-siddhi adp-siddhi-gate" data-testid={`adp-week-siddhi-${slug}`}>
        <p className="adp-siddhi-head"><Lock size={12} strokeWidth={1.8} /> Siddhi — not yet</p>
        <p className="adp-siddhi-body">{w.siddhiGate}</p>
      </div>
    </div>
  );
}

export function MonthPanel({ fx, slug }) {
  const m = fx.month;
  const [identity, setIdentity] = useState(m.retro.identityExample);
  const idRef = React.useRef(null);
  React.useLayoutEffect(() => {
    const el = idRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [identity]);
  return (
    <div className="adp-panel" data-testid={`adp-month-${slug}`}>
      <Disclaimer />
      <div className="adp-sadhana">
        <div className="adp-streak-row">
          <span className="adp-streak"><Flame size={15} strokeWidth={1.8} /> {m.keptLabel}</span>
          <span className="adp-daytag adp-daytag-done">Day 30 · cycle complete</span>
        </div>
        <DayStrip days={m.days} />
        <p className="adp-practice">{fx.practiceLine}</p>
      </div>

      <div className="adp-siddhi" data-testid={`adp-month-siddhi-${slug}`}>
        <p className="adp-siddhi-head"><Target size={13} strokeWidth={1.8} /> Siddhi — your retrospective</p>

        <div className="adp-retro-block">
          <p className="adp-retro-label">Phala — the fruit</p>
          <p className="adp-retro-guess">{m.retro.phalaGuess}</p>
          <p className="adp-retro-actual">{m.retro.phalaActual}</p>
        </div>

        <div className="adp-retro-block">
          <p className="adp-retro-label">Vighna — the obstacle</p>
          <p className="adp-retro-guess">{m.retro.vighnaGuess}</p>
          <p className="adp-retro-actual">{m.retro.vighnaActual}</p>
        </div>

        <div className="adp-evidence" data-testid={`adp-evidence-${slug}`}>
          <Flame size={12} strokeWidth={1.8} /> {m.retro.evidence}
        </div>

        <div className="adp-identity">
          <p className="adp-retro-label">Your identity, in your words</p>
          <textarea
            ref={idRef}
            className="adp-identity-input"
            style={{ overflow: "hidden", resize: "none" }}
            value={identity}
            onChange={(e) => setIdentity(e.target.value)}
            rows={2}
            maxLength={240}
            data-testid={`adp-identity-${slug}`}
            aria-label="Example identity statement (editable)"
          />
        </div>
      </div>
    </div>
  );
}

export default function AgedDemoPreview({ slug }) {
  const fx = getAgedFixture(slug);
  const [mode, setMode] = useState("today"); // today | week | month
  if (!fx) return null;

  return (
    <div className="adp-wrap" data-testid={`adp-${slug}`}>
      <div className="adp-controls" data-testid={`adp-controls-${slug}`}>
        <span className="adp-controls-label">Preview accumulated practice:</span>
        <button
          type="button"
          className={`adp-btn ${mode === "week" ? "is-active" : ""}`}
          onClick={() => setMode(mode === "week" ? "today" : "week")}
          data-testid={`adp-see-week-${slug}`}
        >
          <CalendarClock size={13} strokeWidth={1.8} /> See it after a week
        </button>
        <button
          type="button"
          className={`adp-btn ${mode === "month" ? "is-active" : ""}`}
          onClick={() => setMode(mode === "month" ? "today" : "month")}
          data-testid={`adp-see-month-${slug}`}
        >
          <CalendarCheck size={13} strokeWidth={1.8} /> See it after a month
        </button>
        {mode !== "today" && (
          <button
            type="button"
            className="adp-btn adp-btn-reset"
            onClick={() => setMode("today")}
            data-testid={`adp-reset-${slug}`}
          >
            <RotateCcw size={12} strokeWidth={1.8} /> Back to today
          </button>
        )}
      </div>

      {mode === "week" && <WeekPanel fx={fx} slug={slug} />}
      {mode === "month" && <MonthPanel fx={fx} slug={slug} />}
    </div>
  );
}
